import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Flex,
  Heading,
  toast,
  Text
} from '@sparrowengg/twigs-react';
import { SaveIcon } from '@sparrowengg/twigs-react-icons';
import FileUploads from './file-uploads';
import AppDetails from './app-details';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { defaultValues, youtubeRegex, appTypeConstants } from '../constants';
import { devPortalServices } from 'commons/services';
import { AddLocalDevAppProps } from '../types';

const AddLocalDevApp = ({ app: appProp }: AddLocalDevAppProps) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const appIdFromQuery = searchParams.get('appId');
  const {
    register,
    setValue,
    handleSubmit,
    formState: { errors, dirtyFields, isDirty },
    watch,
    reset,
    getValues
  } = useForm({
    defaultValues
  });
  const [loading, setLoading] = useState(false);
  const [disabled, setIsDisabled] = useState({ publish: true, update: false });
  const [app, setApp] = useState(appProp);
  const [currentAppId, setCurrentAppId] = useState<number | null>(
    appProp?.appId || (appIdFromQuery ? parseInt(appIdFromQuery, 10) : null)
  );

  const convertUrl = (url: string): string => {
    if (youtubeRegex.test(url)) {
      const videoIdMatch = url.match(youtubeRegex);
      const videoId = videoIdMatch ? videoIdMatch[4] : null;
      if (videoId) {
        return `https://www.youtube.com/embed/${videoId}`;
      }
    }
    return url;
  };

  const isEmptyObject = (obj: any): boolean => {
    if (!obj || typeof obj !== 'object') return true;
    return Object.keys(obj).length === 0;
  };

  const prepareFormData = (value: any, isUpdate: boolean = false) => {
    const formData = new FormData();

    // Use default video URL if not provided
    const videoUrl =
      value.videoUrl?.trim() || 'https://www.youtube.com/watch?v=RymrDTbydWc';
    value.videoUrl = convertUrl(videoUrl);

    // Handle all fields
    for (const key in value) {
      if (value.hasOwnProperty(key)) {
        if (key === 'events') {
          // Handle events as array
          if (Array.isArray(value[key]) && value[key].length > 0) {
            value[key].forEach((event: string) => {
              formData.append('events[]', event);
            });
          }
        } else if (key === 'location') {
          // Handle location as locationName
          if (value[key]) {
            formData.append('locationName', value[key]);
          }
        } else if (key === 'iParams') {
          // Handle iParams as JSON string - only send if not empty
          const iParams = value[key];
          if (iParams) {
            if (typeof iParams === 'string' && iParams.trim()) {
              try {
                const parsed = JSON.parse(iParams);
                if (!isEmptyObject(parsed)) {
                  formData.append('iParams', JSON.stringify(parsed));
                }
              } catch {
                // If parsing fails, send as is if not empty
                if (iParams.trim()) {
                  formData.append('iParams', iParams);
                }
              }
            } else if (typeof iParams === 'object' && !isEmptyObject(iParams)) {
              formData.append('iParams', JSON.stringify(iParams));
            }
          }
        } else if (key === 'oauth') {
          // Handle oauth as JSON string - only send if not empty
          const oauth = value[key];
          if (oauth) {
            if (typeof oauth === 'string' && oauth.trim()) {
              try {
                const parsed = JSON.parse(oauth);
                if (!isEmptyObject(parsed)) {
                  formData.append('oauth', JSON.stringify(parsed));
                }
              } catch {
                // If parsing fails, send as is if not empty
                if (oauth.trim()) {
                  formData.append('oauth', oauth);
                }
              }
            } else if (typeof oauth === 'object' && !isEmptyObject(oauth)) {
              formData.append('oauth', JSON.stringify(oauth));
            }
          }
        } else if (key === 'whitelistedDomains') {
          // Handle whitelistedDomains as array - ensure it's a simple array of trimmed strings
          let domains = value[key];

          // If domains is a string, try to parse it
          if (typeof domains === 'string') {
            try {
              domains = JSON.parse(domains);
            } catch {
              // If parsing fails, treat as single domain
              domains = [domains];
            }
          }

          // Ensure it's an array and process each domain
          if (Array.isArray(domains) && domains.length > 0) {
            // Filter out empty strings, trim each domain, and remove duplicates
            const processedDomains = domains
              .map((domain: any) => {
                // If domain is a stringified array, parse it
                if (
                  typeof domain === 'string' &&
                  domain.trim().startsWith('[')
                ) {
                  try {
                    const parsed = JSON.parse(domain);
                    return Array.isArray(parsed) ? parsed : [domain];
                  } catch {
                    return domain.trim();
                  }
                }
                return typeof domain === 'string'
                  ? domain.trim()
                  : String(domain).trim();
              })
              .flat() // Flatten in case we had nested arrays
              .filter((domain: string) => domain.length > 0) // Remove empty strings
              .filter(
                (domain: string, index: number, self: string[]) =>
                  self.indexOf(domain) === index
              ); // Remove duplicates

            // Append each domain as a separate entry (not stringified)
            processedDomains.forEach((domain: string) => {
              formData.append('whitelistedDomains[]', domain);
            });
          }
        } else if (key === 'icon') {
          // Handle icon - only send if it's a new file (File/Blob), not a URL string
          if (value[key]) {
            if (value[key] instanceof File) {
              formData.append(key, value[key]);
            } else if (value[key]?.data) {
              const iconData = value[key].data;
              // Only append if it's a File or Blob, not a URL string
              if (iconData instanceof File || iconData instanceof Blob) {
                formData.append(key, iconData);
              }
              // Skip if iconData is a string (URL) - don't send unchanged icon
            }
          }
        } else {
          formData.append(key, value[key]);
        }
      }
    }

    // For updates, only send icon if it's a new file (File/Blob), not the existing URL
    if (isUpdate) {
      const icon = getValues('icon');
      // Only append icon if it exists, wasn't already added in the loop above, and is a File/Blob
      if (icon && !value.icon) {
        if (icon instanceof File) {
          formData.append('icon', icon);
        } else if (icon && typeof icon === 'object' && 'data' in icon) {
          const iconData = (icon as any).data;
          // Only append if it's a File or Blob, not a URL string
          if (iconData instanceof File || iconData instanceof Blob) {
            formData.append('icon', iconData);
          }
          // Skip if iconData is a string (URL) - don't send unchanged icon
        }
      }
    }

    return formData;
  };

  const submitHandler = async (value: any) => {
    setLoading(true);
    try {
      const appIdToUse = currentAppId || app?.appId;

      // For updates, keep original appName; for creates, use appLabel as appName
      if (appIdToUse) {
        value.appName = app?.name || value.appLabel;
      } else {
        value.appName = value.appLabel;
      }

      if (appIdToUse) {
        // Update existing app
        const formData = prepareFormData(value, true);
        formData.append('appVersionId', app?.appVersionId?.toString() || '');
        formData.append('appId', appIdToUse.toString());

        await devPortalServices.updateLocalDevApp(formData);

        toast({
          variant: 'success',
          description: 'Local dev app has been updated successfully'
        });
        navigate('/');
      } else {
        // Create new app
        const formData = prepareFormData(value, false);
        const response = await devPortalServices.uploadLocalDevApp(formData);

        if (response?.appId) {
          setCurrentAppId(response.appId);
          setApp(response);
          toast({
            variant: 'success',
            description: 'Local dev app has been created successfully'
          });
          // Don't navigate, stay on the page for further editing
        } else {
          toast({
            variant: 'success',
            description: 'Local dev app has been uploaded successfully'
          });
          navigate('/');
        }
      }
    } catch (err) {
      console.error(err);
      toast({
        variant: 'error',
        description: 'Error in file upload, Please try again'
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch app if appId is in query params (only if appProp is not provided)
  // Note: If appProp is provided, it means EditLocalDevApp already fetched it
  useEffect(() => {
    const fetchApp = async () => {
      if (appIdFromQuery && !appProp && !app) {
        try {
          const response = await devPortalServices.getSingleApp(appIdFromQuery);
          const convertToYouTubeURL = (embedUrl: string): string => {
            const embedRegex = /\/embed\/([^/?]+)/;
            const match = embedUrl?.match(embedRegex);
            if (match && match[1]) {
              return `https://www.youtube.com/watch?v=${match[1]}`;
            } else {
              return embedUrl;
            }
          };
          response.videoUrl = convertToYouTubeURL(response?.videoUrl);
          setApp(response);
          setCurrentAppId(response.appId);
        } catch (err) {
          console.error('Error fetching app:', err);
        }
      }
    };

    fetchApp();
  }, [appIdFromQuery, appProp, app]);

  // Set default values for new app creation (not for editing)
  useEffect(() => {
    const setDefaultsForNewApp = async () => {
      // Only set defaults when creating a new app (not editing)
      if (!app && !appIdFromQuery && !appProp) {
        try {
          // Fetch locations and set default
          const locationsResponse = await devPortalServices.getAllLocations();
          if (
            locationsResponse?.data &&
            Array.isArray(locationsResponse.data)
          ) {
            // Find "full_page_app" location
            const fullPageAppLocation = locationsResponse.data.find(
              (loc: any) => loc.name === 'full_page_app'
            );
            if (fullPageAppLocation) {
              setValue('location', 'full_page_app', { shouldDirty: false });
            }
          }
        } catch (err) {
          console.error('Error setting defaults for new app:', err);
        }
      }
    };

    setDefaultsForNewApp();
  }, [app, appIdFromQuery, appProp, setValue]);

  // Update form when app data is available
  useEffect(() => {
    if (app) {
      const {
        name,
        description,
        howToUse,
        icon,
        videoUrl,
        websiteLink,
        appOverView,
        supportEmail,
        supportUrl
      } = app;

      // Extract location from locations array or location.data
      const extractLocation = () => {
        const appAny = app as any;
        // Check if locations is an array of strings
        if (Array.isArray(appAny.locations) && appAny.locations.length > 0) {
          return appAny.locations[0];
        }
        // Check if location.data exists and has items
        if (
          appAny.location?.data &&
          Array.isArray(appAny.location.data) &&
          appAny.location.data.length > 0
        ) {
          return appAny.location.data[0].name;
        }
        // Fallback to locationName or location string
        return appAny.locationName || appAny.location || '';
      };

      // Extract events from events.data structure
      const extractEvents = () => {
        const appAny = app as any;
        if (appAny.events?.data && Array.isArray(appAny.events.data)) {
          // Flatten the nested structure: events.data[].events[].name
          const eventNames: string[] = [];
          appAny.events.data.forEach((category: any) => {
            if (category.events && Array.isArray(category.events)) {
              category.events.forEach((event: any) => {
                if (event.name) {
                  eventNames.push(event.name);
                }
              });
            }
          });
          return eventNames;
        }
        // Fallback to events array if it's already an array
        if (Array.isArray(appAny.events)) {
          return appAny.events;
        }
        return [];
      };

      const formValues = {
        appName: name,
        appLabel:
          (app as any)?.appLabel || (app as any)?.appLable || name || '',
        shortDescription: description,
        appInstructions: howToUse,
        icon: { data: icon },
        videoUrl,
        supportUrl: supportUrl || '',
        appOverView,
        supportEmail,
        websiteLink: websiteLink || '',
        apiUrl: (app as any)?.apiUrl || '',
        location: extractLocation(),
        events: extractEvents(),
        iParams: (() => {
          const appAny = app as any;
          // Check for iparams (lowercase) first, then iParams (camelCase)
          const iParams = appAny.iparams || appAny.iParams;
          if (typeof iParams === 'string') {
            try {
              return JSON.parse(iParams);
            } catch {
              return {};
            }
          }
          // If it's already an object, return it directly
          if (iParams && typeof iParams === 'object') {
            return iParams;
          }
          return {};
        })(),
        oauth: (() => {
          const appAny = app as any;
          // Check for oauth (lowercase) first, then oAuth (camelCase)
          const oauth = appAny.oauth || appAny.oAuth;
          if (typeof oauth === 'string') {
            try {
              return JSON.parse(oauth);
            } catch {
              return {};
            }
          }
          // If it's already an object, return it directly
          if (oauth && typeof oauth === 'object') {
            return oauth;
          }
          return {};
        })(),
        whitelistedDomains: (() => {
          const appAny = app as any;
          // Check for whitelistedDomains or whitelisted_domains
          const domains =
            appAny.whitelistedDomains || appAny.whitelisted_domains;
          if (Array.isArray(domains)) {
            return domains;
          }
          if (typeof domains === 'string') {
            try {
              const parsed = JSON.parse(domains);
              return Array.isArray(parsed) ? parsed : [];
            } catch {
              return [];
            }
          }
          return [];
        })()
      };
      reset({
        ...formValues
      });
      if (app && (currentAppId || app?.appId)) {
        setIsDisabled({ publish: false, update: false });
      }
    } else if (!appIdFromQuery && !appProp) {
      reset(defaultValues);
    }
  }, [app, appIdFromQuery, appProp, currentAppId]);

  useEffect(() => {
    if ((app || currentAppId) && !isDirty) {
      setIsDisabled({ publish: false, update: true });
    } else {
      setIsDisabled((prev) => ({ ...prev, update: false }));
    }
  }, [isDirty, app, currentAppId]);

  return (
    <Flex
      justifyContent="center"
      css={{ marginTop: '$24', paddingBottom: '$20' }}
    >
      <Box css={{ maxWidth: 1200, width: '100%', padding: '$0 $20' }}>
        <form onSubmit={handleSubmit(submitHandler)}>
          <Flex
            alignItems="center"
            justifyContent="space-between"
            css={{
              paddingBlock: '$5'
            }}
          >
            <Flex flexDirection="column" gap="$2">
              <Heading size="h5">Local Dev App Information</Heading>
              <Text size="md" css={{ color: '$neutral800' }}>
                Enter all the required fields to{' '}
                {app || currentAppId ? 'update' : 'upload'} your local
                development app
              </Text>
            </Flex>
            <Flex alignItems="center" gap="$4">
              <Button
                size="lg"
                type="submit"
                loading={loading}
                disabled={disabled.publish || disabled.update}
                rightIcon={<SaveIcon />}
              >
                {app || currentAppId ? 'Update Changes' : 'Save Changes'}
              </Button>
            </Flex>
          </Flex>
          <Flex css={{ paddingBlock: '$20' }}>
            <FileUploads
              getValues={getValues}
              setValue={setValue}
              watch={watch}
            />
            <Box css={{ flex: 1 }}>
              <AppDetails
                app={app}
                register={register as any}
                errors={errors as any}
                watch={watch as any}
                isDirty={isDirty}
                setValue={setValue as any}
                getValues={getValues as any}
                setIsDisabled={setIsDisabled}
              />
            </Box>
          </Flex>
        </form>
      </Box>
    </Flex>
  );
};

export default AddLocalDevApp;
